<template>
  <div class="condominium-apartments">
    <h2 :id="scrollToId">Vapaat asunnot</h2>
    <div class="condominium-apartments__content">
      <div class="condominium-apartments__content__filters">
        <div class="filter-container">
          <sorting
            :order-by="sortingFilters.orderBy"
            :order="sortingFilters.order"
            feature-group="newHousing"
            mode="sale"
            @sortingChanged="handleSortingChanged"
          ></sorting>
        </div>
      </div>

      <div class="condominium-apartments__content__list">
        <RouterLink
          v-for="(apartment, index) in apartmentsFiltered"
          :key="`condominium-apartment-${index}`"
          :class="`apartment-row ${showAll ? 'show-all' : ''}`"
          :to="`/kohde/${apartment.id}`"
        >
          <!-- Apartment image -->
          <span
            :class="`apartment-image ${apartment.imageUrl ? '' : 'placeholder'}`"
            :style="`background-image: url(${
              apartment.imageUrl || 'https://op-koti.fi/assets/image-placeholder2.svg'
            })`"
          >
            <span class="condominium-apartments-showings">
              <showings :showings="apartment.showings" :show-end-time="true"></showings>
            </span>
          </span>

          <!-- Apartment room description -->
          <span class="apartment-data">
            <span>{{ apartment.rooms }}</span>
            <span>Tyyppi</span>
          </span>

          <!-- Apartment area -->
          <span class="apartment-data">
            <span>
              <area-with-units
                :size="apartment.livingArea.size"
                :unit="apartment.livingArea.displayUnit"
              ></area-with-units>
            </span>
            <span>Koko</span>
          </span>

          <!-- Apartment floor -->
          <span class="apartment-data">
            <span>{{ apartment.floor || '-' }}</span>
            <span>Kerros</span>
          </span>

          <!-- Sales price -->
          <span class="apartment-data">
            <span>{{ $filters.euro(apartment.price, { dashify: true }) }}</span>
            <span>Myyntihinta</span>
          </span>

          <!-- Debt free sales price -->
          <span class="apartment-data">
            <span>{{ $filters.euro(apartment.debtFreePrice || 0, { dashify: true }) }}</span>
            <span>Velaton hinta</span>
          </span>

          <!-- Arrow icon -->
          <span class="apartment-data">
            <i class="icon-chevron-right"></i>
          </span>
        </RouterLink>

        <div class="show-all-apartments" v-if="apartmentsFiltered.length && apartmentsFiltered.length > 6">
          <form-button
            :text="showAll ? 'Piilota' : 'Näytä kaikki'"
            theme="default"
            @click.native="showAll = !showAll"
          ></form-button>
        </div>

        <div v-if="apartmentsFiltered.length === 0" class="no-condominium-apartment-results">
          Ei hakua vastaavia vapaita asuntoja
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Sorting from '@/app/components/sorting/sorting.vue';
import AreaWithUnits from '@/app/components/area-with-units/area-with-units.vue';
import Showings from '@/app/components/showings/showings.vue';
import type { SortingOption } from '@/config/sorting';
import { computed, defineAsyncComponent, ref, type Ref } from 'vue';
import { DateTime } from 'luxon';

const FormButton = defineAsyncComponent(() => import('@/app/components/form-button/form-button.vue'));

const { newHousingInfo } = defineProps({
  newHousingInfo: {
    type: Object,
    required: true,
  },
  scrollToId: {
    type: String,
    required: true,
  },
});

const showAll = ref(false);

const sortingFilters: Ref<Partial<SortingOption>> = ref({
  orderBy: 'price',
  order: 'asc',
});

const apartmentsFiltered = computed(() => {
  return newHousingInfo?.listings?.toSorted((a: any, b: any) => {
    const getValue = (apartment: any) => {
      switch (sortingFilters.value.orderBy) {
        case 'livingArea':
          return apartment.livingArea.size;
        case 'price':
          return apartment.debtFreePrice || apartment.price;
        case 'created': {
          const parsedDate = DateTime.fromISO(apartment[sortingFilters.value.orderBy]);
          return parsedDate.isValid ? parsedDate.toMillis() : 0;
        }
        default:
          return null;
      }
    };

    const aValue = getValue(a);
    const bValue = getValue(b);

    return sortingFilters.value.order === 'asc' ? aValue - bValue : bValue - aValue;
  });
});

function handleSortingChanged(sorting: SortingOption) {
  sortingFilters.value = sorting;
}
</script>
